import { Popover } from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import styles from './styles.scoped.css';

const PRESET_OPTIONS = [
  { label: 'Hari Ini', value: 'today' },
  { label: '7 Hari Terakhir', value: 'last-7-days' },
  { label: '30 Hari Terakhir', value: 'last-30-days' },
  { label: 'Kustom', value: 'custom' },
];

function getPresetRange(preset) {
  const now = dayjs();

  switch (preset) {
    case 'last-7-days':
      return [now.subtract(7, 'day').toDate(), now.toDate()];
    case 'last-30-days':
      return [now.subtract(29, 'day').toDate(), now.toDate()];
    case 'custom':
      return [null, null];
    case 'today':
    default:
      return [now.toDate(), now.toDate()];
  }
}

function isSameDate(left, right) {
  if (!left && !right) {
    return true;
  }

  if (!left || !right) {
    return false;
  }

  return dayjs(left).format('YYYY-MM-DD') === dayjs(right).format('YYYY-MM-DD');
}

function detectPreset(range = []) {
  const [startDate, endDate] = range;

  return (
    PRESET_OPTIONS.find((option) => {
      if (option.value === 'custom') {
        return false;
      }

      const [presetStart, presetEnd] = getPresetRange(option.value);
      return isSameDate(startDate, presetStart) && isSameDate(endDate, presetEnd);
    })?.value || 'custom'
  );
}

function getPresetLabel(preset) {
  return PRESET_OPTIONS.find((option) => option.value === preset)?.label || 'Tanggal';
}

function formatRangeLabel(range = []) {
  const [startDate, endDate] = range;

  if (!startDate || !endDate) {
    return 'Pilih tanggal';
  }

  if (isSameDate(startDate, endDate)) {
    return dayjs(startDate).format('DD MMM YYYY');
  }

  return `${dayjs(startDate).format('DD MMM YYYY')} – ${dayjs(endDate).format('DD MMM YYYY')}`;
}

function getPresetDescription(preset) {
  if (preset === 'custom') {
    return '';
  }

  return formatRangeLabel(getPresetRange(preset));
}

export default function DateFilterField({ range, onChange }) {
  const [opened, setOpened] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(detectPreset(range));
  const [showCustomPicker, setShowCustomPicker] = useState(false);

  const rangeLabel = useMemo(() => formatRangeLabel(range), [range]);
  const triggerLabel = useMemo(
    () => (selectedPreset === 'custom' ? 'Tanggal' : getPresetLabel(selectedPreset)),
    [selectedPreset]
  );
  const popoverWidth = showCustomPicker ? 'max-content' : 'target';

  const handlePresetClick = (preset) => {
    setSelectedPreset(preset);

    if (preset === 'custom') {
      setShowCustomPicker(true);
      return;
    }

    setShowCustomPicker(false);
    onChange(getPresetRange(preset));
    setOpened(false);
  };

  const handleRangeChange = (value) => {
    onChange(value);

    if (value?.[0] && value?.[1]) {
      setSelectedPreset('custom');
      setShowCustomPicker(false);
      setOpened(false);
    }
  };

  const handleTogglePopover = () => {
    setShowCustomPicker(false);
    setOpened((current) => !current);
  };

  return (
    <Popover
      onChange={(value) => {
        setOpened(value);

        if (!value) {
          setShowCustomPicker(false);
        }
      }}
      opened={opened}
      position="bottom-start"
      shadow="md"
      width={popoverWidth}
      withArrow={false}
      withinPortal
      zIndex={1000}
    >
      <Popover.Target>
        <button
          className={styles.dateTrigger}
          onClick={handleTogglePopover}
          type="button"
        >
          <span className={styles.dateTriggerText}>
            <span className={styles.dateTriggerLabel}>{triggerLabel}</span>
            <span className={styles.dateTriggerValue}>{rangeLabel}</span>
          </span>
          <span className={styles.dateTriggerIcon}>
            <img alt="" aria-hidden="true" src="assets/ic-arrow-updown.svg" />
          </span>
        </button>
      </Popover.Target>
      <Popover.Dropdown
        className={[
          styles.datePopover,
          showCustomPicker ? styles.datePopoverCustom : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {!showCustomPicker && (
          <div className={styles.datePresetList}>
            {PRESET_OPTIONS.map((option) => (
              <button
                className={[
                  styles.datePresetButton,
                  selectedPreset === option.value ? styles.datePresetButtonActive : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                key={option.value}
                onClick={() => handlePresetClick(option.value)}
                type="button"
              >
                <span className={styles.datePresetTitle}>{option.label}</span>
                {getPresetDescription(option.value) && (
                  <span className={styles.datePresetDescription}>
                    {getPresetDescription(option.value)}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
        {showCustomPicker && (
          <div className={styles.datePopoverPicker}>
            <DatePicker
              allowSingleDateInRange
              className={styles.customDateRange}
              defaultDate={dayjs().subtract(1, 'month').startOf('month').toDate()}
              locale="id"
              maxDate={new Date()}
              numberOfColumns={2}
              onChange={handleRangeChange}
              type="range"
              value={range}
            />
          </div>
        )}
      </Popover.Dropdown>
    </Popover>
  );
}

DateFilterField.propTypes = {
  onChange: PropTypes.func,
  range: PropTypes.array,
};
